import { environment } from 'environment';
import Cookies from 'js-cookie';
import { v4 as uuid } from 'uuid';

type ApiConfig<P = void> = RequestInit & {
  params?: P extends void ? { [key: string]: string | boolean | number } : P;
  responseType?: 'json' | 'text' | 'blob' | 'formData';
  /** returns just res */
  softFail?: boolean;
};

type HTTPMethod = 'GET' | 'POST' | 'DELETE' | 'PATCH' | 'PUT' | 'HEAD' | 'OPTIONS';

// why must safari always be an absolute waffle
const safariCookies = {
  visitor: `${environment.cookies.prefix}safari_sucks__visitor`,
  loggedIn: `${environment.cookies.prefix}safari_sucks__logged_in`,
  opts: { sameSite: 'strict', expires: 3650 },
} as const;

// duplicated in apps/web/scripts/tracking.tsx - change there too
const storage = {
  visitor: `b4k-visitor`,
  loggedIn: `${environment.cookies.prefix}b4k-logged-in`,
} as const;

class NetEssentials {
  private static _visitorId: string;
  private static _loggedIn: boolean | undefined;
  private static _isSafari =
    typeof window !== 'undefined' && typeof navigator !== 'undefined'
      ? /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
      : undefined;

  static get visitorId() {
    if (this._visitorId) return this._visitorId;

    const visitorId = localStorage.getItem(storage.visitor);

    if (visitorId) {
      if (!this._visitorId) this._visitorId = visitorId;
      if (this._isSafari && !Cookies.get(safariCookies.visitor))
        Cookies.set(safariCookies.visitor, this._visitorId, safariCookies.opts);

      return this._visitorId;
    }

    if (this._isSafari) {
      const hasCookie = Cookies.get(safariCookies.visitor);

      if (hasCookie) {
        this._visitorId = hasCookie;
        localStorage.setItem(storage.visitor, this._visitorId);
        return this._visitorId;
      }
    }

    // this._visitorId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    this._visitorId = uuid();
    localStorage.setItem(storage.visitor, this._visitorId);
    if (this._isSafari) Cookies.set(safariCookies.visitor, this._visitorId, safariCookies.opts);

    return this._visitorId;
  }

  static __setVisitorId(visitorId: string) {
    this._visitorId = visitorId;
    localStorage.setItem(storage.visitor, visitorId);
    if (this._isSafari) Cookies.set(safariCookies.visitor, visitorId, safariCookies.opts);
  }

  static get loggedIn() {
    if (typeof this._loggedIn === 'boolean') return this._loggedIn;

    const loggedIn = localStorage.getItem(storage.loggedIn);
    if (loggedIn) return true;

    if (!this._isSafari) return false;
    return !!Cookies.get(safariCookies.loggedIn);
  }

  static set loggedIn(value: boolean) {
    this._loggedIn = value;

    if (value) {
      localStorage.setItem(storage.loggedIn, 'true');
      if (this._isSafari) Cookies.set(safariCookies.loggedIn, 'true', safariCookies.opts);
    } else {
      localStorage.removeItem(storage.loggedIn);
      if (this._isSafari) Cookies.remove(safariCookies.loggedIn);
    }
  }
}

export { NetEssentials, type ApiConfig, type HTTPMethod };
